/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useLocation, useParams } from 'react-router-dom';

import { useTariff } from '../../../context/TariffContext';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import charger from '../../../utils/api/charger';

/**
 * Interface representing an active price component from tariffs API
 */
export interface ActivePriceComponent {
  type: string;
  price: number | string;
  vat?: number | string;
  step_size?: number;
}

/**
 * Interface representing tariff fee details from tariffs[0].fees
 */
export interface TariffFeeDetails {
  platformFee?: number | string | null;
  platformFeeType?: string;
  paymentSurcharge?: number | string | null;
  paymentSurchargeType?: string;
  sessionFee?: number | string | null;
  sessionFeeType?: string;
  currency?: string;
  platformFeeMissing?: boolean;
  platformFeeByCurrency?: Array<{
    currency: string;
    amount: number;
  }>;
}

/**
 * Interface representing an individual tariff item from tariffs array
 */
export interface TariffItem {
  tariff_id?: string;
  currency?: string;
  tariff_currency?: string;
  fx_rate?: number;
  fx_rate_source?: string;
  tariff_type?: string;
  active_price_components?: ActivePriceComponent[];
  fees?: TariffFeeDetails;
}

/**
 * Interface representing the tariff API response structure
 * Note: Top-level fees object is intentionally ignored.
 */
export interface TariffApiResponse {
  tariffs?: TariffItem[];
  fees?: any;
}

export interface TariffFeesInformation {
  platformFee?: number | string | null;
  platformFeeType?: string;
  sessionFee?: number | string | null;
  sessionFeeType?: string;
  currencyType?: string;
}

export interface TariffFeesOverviewProperties {
  /** Optional explicit EVSE ID / Device ID override (auto-detected from route/query params if omitted) */
  evseId?: string | number;
  /** Optional pre-fetched tariff API response object */
  tariffData?: TariffApiResponse | null;
  /** Unit price for energy (per kWh) fallback */
  energyPrice?: string | number | null;
  /** Fee breakdown object fallback */
  fees?: TariffFeesInformation | null;
  /** Explicit platform fee fallback */
  platformFee?: number | string | null;
  /** Explicit platform fee type fallback ('percentage' | 'amount' | 'flatRate') */
  platformFeeType?: string;
  /** Explicit session / surcharge fee fallback */
  sessionFee?: number | string | null;
  /** Explicit session / surcharge fee type fallback ('percentage' | 'amount' | 'flatRate') */
  sessionFeeType?: string;
  /** Currency symbol or code override (e.g., 'SAR') */
  currency?: string;
  /** Optional pre-formatted text override for the energy rate card */
  energyRateText?: string;
  /** Optional pre-formatted text override for the platform fee card */
  platformFeeText?: string;
  /** Optional pre-formatted text override for the surcharge fee card */
  surchargeFeeText?: string;
  /** Optional additional CSS class names */
  className?: string;
}

interface ParsedTariffState {
  energyRateText: string;
  platformFeeText: string;
  surchargeFeeText: string;
}

interface RouterContextListenerProperties {
  /** Callback fired when router parameters or location change */
  onChange: (data: { deviceId?: string; search: string }) => void;
}

/**
 * Helper component that unconditionally subscribes to router hooks when inside a Router context.
 * Prevents throwing errors when TariffFeesOverview is mounted outside a Router.
 * @param properties - Component properties.
 * @returns Renders nothing.
 */
function RouterContextListener(
  properties: Readonly<RouterContextListenerProperties>,
): null {
  const { onChange } = properties;
  const routeParameters = useParams<{ deviceId?: string }>();
  const location = useLocation();

  const currentDeviceId = routeParameters.deviceId;
  const currentSearch = location.search;

  useEffect(() => {
    onChange({
      deviceId: currentDeviceId,
      search: currentSearch,
    });
  }, [currentDeviceId, currentSearch, onChange]);

  return null;
}

/**
 * Reusable Mobile Dynamic Tariff & Fees Overview Component.
 * Autonomously fetches and parses rates and fees exclusively from tariffs[0],
 * ignoring any root-level fees object.
 * @param properties - Component properties.
 * @returns JSX Element containing Tariff & Fees cards.
 */
function TariffFeesOverview(
  properties: Readonly<TariffFeesOverviewProperties> = {},
): JSX.Element {
  const {
    evseId,
    tariffData: passedTariffData,
    energyPrice = null,
    fees = null,
    platformFee = null,
    platformFeeType = null,
    sessionFee = null,
    sessionFeeType = null,
    currency = '',
    energyRateText,
    platformFeeText,
    surchargeFeeText,
    className = '',
  } = properties;

  const { t } = useTranslation('charger');
  const tReference = useRef(t);
  tReference.current = t;

  const inRouter = useInRouterContext();

  const [routerData, setRouterData] = useState<{ deviceId?: string; search: string }>(
    () => ({
      deviceId: '',
      search: typeof window !== 'undefined' ? window.location.search : '',
    }),
  );

  /**
   * Updates state when router parameters change.
   * @param data - Updated router parameters.
   */
  const handleRouterChange = useCallback(
    (data: { deviceId?: string; search: string }): void => {
      setRouterData((previous) => {
        if (
          previous.deviceId === data.deviceId &&
          previous.search === data.search
        ) {
          return previous;
        }
        return data;
      });
    },
    [],
  );

  /**
   * Resolves target EVSE / device identifier from props, route params, or query parameters.
   */
  const targetEvseId = useMemo((): string => {
    if (evseId !== undefined && evseId !== null && String(evseId).trim() !== '') {
      return String(evseId).trim();
    }
    if (routerData.deviceId && routerData.deviceId.trim() !== '') {
      return routerData.deviceId.trim();
    }
    const queryParameters = new URLSearchParams(
      routerData.search || (typeof window !== 'undefined' ? window.location.search : ''),
    );
    const queryDeviceId = queryParameters.get('deviceId');
    if (queryDeviceId && queryDeviceId.trim() !== '') {
      return queryDeviceId.trim();
    }
    const storedDeviceId = getLocalStorageItem('deviceId');
    if (storedDeviceId && String(storedDeviceId).trim() !== '') {
      return String(storedDeviceId).trim();
    }
    return '';
  }, [evseId, routerData.deviceId, routerData.search]);

  const [loading, setLoading] = useState<boolean>(!passedTariffData && Boolean(targetEvseId));
  const [fetchedTariff, setFetchedTariff] = useState<ParsedTariffState | null>(null);

  /**
   * Parses the tariff API response, strictly extracting fields from tariffs[0]
   * and completely ignoring any root-level fees object.
   * @param rawResponse - API response object or tariffs container.
   * @returns Parsed display texts or null if unparseable.
   */
  const extractTariffFromResponse = useCallback(
    (rawResponse: any): ParsedTariffState | null => {
      const tariff =
        rawResponse?.tariffs?.[0] ||
        (Array.isArray(rawResponse) ? rawResponse[0] : null);

      if (!tariff) {
        return null;
      }

      const effectiveCurrency =
        currency || tariff.currency || tariff.fees?.currency || tReference.current('currency') || 'SAR';

      // 1. Energy Rate from tariffs[0].active_price_components (type === 'ENERGY')
      let computedEnergyText = '-';
      const energyComponent =
        tariff.active_price_components?.find(
          (component: ActivePriceComponent) =>
            component.type?.toUpperCase() === 'ENERGY',
        ) || tariff.active_price_components?.[0];

      if (
        energyComponent?.price !== undefined &&
        energyComponent?.price !== null &&
        energyComponent?.price !== ''
      ) {
        const numericEnergy = Number(energyComponent.price);
        if (!Number.isNaN(numericEnergy)) {
          computedEnergyText = `${numericEnergy.toFixed(2)} ${effectiveCurrency} / kWh`;
        }
      }

      // 2. Platform Fee strictly from tariffs[0].fees
      let computedPlatformText = '-';
      const tariffFees = tariff.fees;
      const rawPlatformFee = tariffFees?.platformFee;
      const effectivePlatformType = tariffFees?.platformFeeType || 'amount';

      if (
        rawPlatformFee !== undefined &&
        rawPlatformFee !== null &&
        rawPlatformFee !== ''
      ) {
        if (effectivePlatformType === 'percentage') {
          computedPlatformText = `${rawPlatformFee}%`;
        } else {
          const numericPlatform = Number(rawPlatformFee);
          computedPlatformText = Number.isNaN(numericPlatform)
            ? `${rawPlatformFee}`
            : `${effectiveCurrency} ${numericPlatform.toFixed(2)}`;
        }
      }

      // 3. Surcharge Fee strictly from tariffs[0].fees (paymentSurcharge)
      let computedSurchargeText = '-';
      const rawSurchargeFee =
        tariffFees?.paymentSurcharge !== undefined &&
        tariffFees?.paymentSurcharge !== null
          ? tariffFees.paymentSurcharge
          : tariffFees?.sessionFee;
      const effectiveSurchargeType =
        tariffFees?.paymentSurchargeType ||
        tariffFees?.sessionFeeType ||
        'percentage';

      if (
        rawSurchargeFee !== undefined &&
        rawSurchargeFee !== null &&
        rawSurchargeFee !== ''
      ) {
        if (effectiveSurchargeType === 'percentage') {
          computedSurchargeText = `${rawSurchargeFee}%`;
        } else {
          const numericSurcharge = Number(rawSurchargeFee);
          computedSurchargeText = Number.isNaN(numericSurcharge)
            ? `${rawSurchargeFee}`
            : `${effectiveCurrency} ${numericSurcharge.toFixed(2)}`;
        }
      }

      return {
        energyRateText: computedEnergyText,
        platformFeeText: computedPlatformText,
        surchargeFeeText: computedSurchargeText,
      };
    },
    [currency],
  );

  const tariffContext = useTariff();

  const activeTariffState = useMemo((): ParsedTariffState | null => {
    if (passedTariffData) {
      return extractTariffFromResponse(passedTariffData);
    }
    if (tariffContext?.rawTariffData) {
      return extractTariffFromResponse(tariffContext.rawTariffData);
    }
    return fetchedTariff;
  }, [passedTariffData, tariffContext?.rawTariffData, fetchedTariff, extractTariffFromResponse]);

  const isEffectiveLoading = Boolean(
    (tariffContext?.isLoading && !tariffContext?.rawTariffData) ||
      (loading && !tariffContext?.rawTariffData && !passedTariffData),
  );

  useEffect(() => {
    let isSubscribed = true;

    if (passedTariffData || tariffContext?.rawTariffData || tariffContext?.isLoading) {
      if (isSubscribed) {
        setLoading(false);
      }
      return () => {
        isSubscribed = false;
      };
    }

    if (!targetEvseId) {
      setLoading(false);
      return () => {
        isSubscribed = false;
      };
    }

    setLoading(true);

    /**
     * Executes the asynchronous tariff fetch.
     */
    const runFetch = async () => {
      try {
        const response = await charger.fetchTariffByEvse(targetEvseId);
        const data = response?.data?.data || response?.data;
        if (isSubscribed) {
          const parsed = extractTariffFromResponse(data);
          setFetchedTariff(parsed);
          setLoading(false);
        }
      } catch (error: any) {
        const isTokenError =
          error?.response?.status === 401 ||
          error?.response?.data?.message?.includes('PENDING_REGISTRATION') ||
          error?.response?.data?.message?.includes('Adhoc token');

        if (isTokenError) {
          try {
            const sessionResponse = await charger.createAdhocSession({
              evseUid: targetEvseId,
            });
            const sessionData =
              sessionResponse?.data?.data || sessionResponse?.data;
            if (sessionData?.token) {
              setLocalStorageItem('token', sessionData.token);
              setLocalStorageItem('adhocToken', sessionData.token);
            }
            const retryResponse = await charger.fetchTariffByEvse(targetEvseId);
            const retryData = retryResponse?.data?.data || retryResponse?.data;
            if (isSubscribed) {
              const parsed = extractTariffFromResponse(retryData);
              setFetchedTariff(parsed);
              setLoading(false);
            }
            return;
          } catch (retryError) {
            console.error('Error retrying tariff fetch in TariffFeesOverview:', retryError);
          }
        } else {
          console.error('Error fetching tariff in TariffFeesOverview:', error);
        }
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    runFetch();

    return () => {
      isSubscribed = false;
    };
  }, [passedTariffData, targetEvseId, extractTariffFromResponse]);

  const effectiveFallbackCurrency =
    currency || fees?.currencyType || t('currency') || 'SAR';

  /**
   * Formats the energy rate card value
   * @returns Formatted energy rate
   */
  const renderEnergyRate = (): string => {
    if (energyRateText !== undefined && energyRateText !== null) {
      return energyRateText;
    }
    if (activeTariffState?.energyRateText) {
      return activeTariffState.energyRateText;
    }
    if (energyPrice === undefined || energyPrice === null || energyPrice === '') {
      return '-';
    }
    const numericPrice = Number(energyPrice);
    if (Number.isNaN(numericPrice)) {
      return '-';
    }
    return `${numericPrice.toFixed(2)} ${effectiveFallbackCurrency} / kWh`;
  };

  /**
   * Formats the platform fee card value
   * @returns Formatted platform fee
   */
  const renderPlatformFee = (): string => {
    if (platformFeeText !== undefined && platformFeeText !== null) {
      return platformFeeText;
    }
    if (activeTariffState?.platformFeeText) {
      return activeTariffState.platformFeeText;
    }
    const rawValue =
      platformFee !== null && platformFee !== undefined ? platformFee : fees?.platformFee;
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }
    const effectiveType = platformFeeType || fees?.platformFeeType || 'percentage';
    if (effectiveType === 'percentage') {
      return `${rawValue}%`;
    }
    return `${effectiveFallbackCurrency} ${Number(rawValue).toFixed(2)}`;
  };

  /**
   * Formats the surcharge fee card value
   * @returns Formatted surcharge fee
   */
  const renderSurchargeFee = (): string => {
    if (surchargeFeeText !== undefined && surchargeFeeText !== null) {
      return surchargeFeeText;
    }
    if (activeTariffState?.surchargeFeeText) {
      return activeTariffState.surchargeFeeText;
    }
    const rawValue =
      sessionFee !== null && sessionFee !== undefined ? sessionFee : fees?.sessionFee;
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }
    const effectiveType = sessionFeeType || fees?.sessionFeeType || 'percentage';
    if (effectiveType === 'percentage') {
      return `${rawValue}%`;
    }
    return `${effectiveFallbackCurrency} ${Number(rawValue).toFixed(2)}`;
  };

  return (
    <div className={`tariff-fees-section ${className}`.trim()}>
      {inRouter && <RouterContextListener onChange={handleRouterChange} />}
      <p className="header">{t('tariffAndFees')} :</p>

      <div className="tariff-cards-row">
        {/* Card 1: Energy Rate */}
        <div className={`tariff-rate-card ${isEffectiveLoading ? 'loading' : ''}`.trim()}>
          <p className="card-label">{t('energyRate')}</p>
          <h6 className="card-value" dir="ltr">
            {isEffectiveLoading ? '...' : renderEnergyRate()}
          </h6>
        </div>

        {/* Card 2: Platform Fee */}
        <div className={`tariff-rate-card ${isEffectiveLoading ? 'loading' : ''}`.trim()}>
          <p className="card-label">{t('platformFee')}</p>
          <h6 className="card-value" dir="ltr">
            {isEffectiveLoading ? '...' : renderPlatformFee()}
          </h6>
        </div>

        {/* Card 3: Surcharge Fee */}
        <div className={`tariff-rate-card ${isEffectiveLoading ? 'loading' : ''}`.trim()}>
          <p className="card-label">{t('surchargeFee')}</p>
          <h6 className="card-value" dir="ltr">
            {isEffectiveLoading ? '...' : renderSurchargeFee()}
          </h6>
        </div>
      </div>
    </div>
  );
}

export default TariffFeesOverview;
